import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Badge, BadgeVariant } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconApplicants } from '@components/icons/applicants';
import { IconDate } from '@components/icons/date';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconHelp } from '@components/icons/help';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPerson } from '@components/icons/person';
import { IconPhone } from '@components/icons/phone';
import { IconRedo } from '@components/icons/redo';
import { IconRole } from '@components/icons/role';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconSwap } from '@components/icons/swap';
import { IconTrash } from '@components/icons/trash';
import { LabelAvatarNameGroup } from '@components/label-avatar-name-group';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { hideModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Table, TableProps } from '@components/table';
import { usePersistedTableStore } from '@components/table/table-store';
import { AvailableColumn } from '@components/table/types';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { capitalizeFirstLetter } from '@utils/strings';

import { useTableView } from 'api/authentication/hooks';
import { useInviteUser, useRoleOptions, useUsers } from 'api/users/hooks';
import { User, UserRole } from 'api/users/requests';
import { usePermissions } from 'contexts/permissions-context';

import { RolesInfoTab } from './roles-info';
import { useDeleteTeamUserModal } from './user-delete-modal';
import { useUserDetailsSidebar } from './user-details-sidebar';
import { useEditTeamUserSidebar } from './user-edit-sidebar';
import { useRevokeTeamUserModal } from './user-revoke-modal';
import { useTeamUserSidebar } from './user-sidebar';

export const UserStatus = ({ status }: { status: 'joined' | 'created' | 'invited' }) => {
  const { t } = useScopedTranslate(locale);
  const text = t(status);
  const color =
    status === 'joined'
      ? 'elementGreen200'
      : status === 'invited'
      ? 'elementBlue100'
      : 'elementOrange100';
  return (
    <Stack orientation="horizontal" align="center">
      <IconStatusIndicator color={color} />
      <Text text={text} font="regular14" color={color} />
    </Stack>
  );
};

export const TeamList = () => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');
  const location = useLocation();
  const activeTab = location.pathname.split('/').pop();

  const { data: users, isLoading } = useUsers();
  if (isLoading || !users) {
    return <Spinner />;
  }

  const activeUsers = users.filter((user) => {
    return user.lastLogin;
  });
  const invitedUsers = users.filter((user) => {
    return !user.lastLogin;
  });

  const tabs = [
    {
      label: t('members'),
      section: 'users',
      counter: activeUsers.length,
      icon: <IconApplicants />,
      content: <UserList searchStr={searchStr} users={activeUsers} />,
    },
    {
      label: t('invitedTab'),
      section: 'invited',
      counter: invitedUsers.length,
      icon: <IconSend />,
      content: <InvitedUserList searchStr={searchStr} users={invitedUsers} />,
    },
    {
      label: t('rolesTab'),
      section: 'roles',
      icon: <IconRole />,
      content: <RolesInfoTab />,
    },
  ];

  const renderActions = () => {
    if (activeTab === 'roles') {
      return <RolesTabActions />;
    }
    return <UserListActions setSearchStr={setSearchStr} searchStr={searchStr} />;
  };

  return (
    <RouterTabsHeader
      actions={renderActions()}
      breadcrumbs={<Breadcrumb items={[{ label: t('members') }]} />}
      tabs={tabs}
    />
  );
};

type UserListProps = { searchStr: string; users: Array<User> };

const ROLE_VARIANT_MAP: Record<UserRole, BadgeVariant> = {
  admin: 'indigo',
  editor: 'blue',
  contributor: 'orange',
};

const getRoleVariant = (role: UserRole): BadgeVariant => ROLE_VARIANT_MAP[role];

const UserList = ({ searchStr, users }: UserListProps) => {
  const { t } = useScopedTranslate(locale);
  const showEditUserSidebar = useEditTeamUserSidebar();
  const showUserDetailsSidebar = useUserDetailsSidebar();
  const showDeleteModal = useDeleteTeamUserModal();
  const { tableView, setTableView } = useTableView('userList');
  const tableStore = usePersistedTableStore({
    tableView,
    setTableView,
  });
  const roleOptions = useRoleOptions();

  const availableColumns: Array<AvailableColumn<User>> = useMemo(
    () => [
      {
        id: 'name',
        title: t('core.fullName'),
        type: 'user',
        Cell: ({ row: user }) => (
          <LabelAvatarNameGroup
            hideLabel
            label={t('core.fullName')}
            name={user.name}
            image={user.picture}
            onClick={() => showUserDetailsSidebar({ user, users })}
          />
        ),
      },
      {
        id: 'email',
        title: t('core.email'),
        type: 'email',
      },
      {
        id: 'phone',
        title: t('core.phone'),
        type: 'phone_number',
      },
      {
        id: 'role',
        title: t('role'),
        renderHeaderIcon: () => <IconRole size={16} />,
        type: 'custom',
        Cell: ({ row: user }) => (
          <Badge
            variant={getRoleVariant(user.role)}
            text={capitalizeFirstLetter(user.role)}
            size="md"
          />
        ),
      },
    ],
    [t, showUserDetailsSidebar, users]
  );

  const endFixedColumns: TableProps<User>['endFixedColumns'] = useMemo(
    () => [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: ({ row: user }) => (
          <Menu
            trigger={IconButton}
            triggerProps={{ variant: 'ghost-purple', size: 'md', icon: <IconMoreVertical /> }}
          >
            <Menu.Item
              label={t('core.edit')}
              icon={<IconEdit />}
              onSelect={() => showEditUserSidebar({ user: user })}
            />
            {!user.isMe && (
              <Menu.Item
                variant="danger"
                label={t('deleteMember')}
                icon={<IconTrash />}
                onSelect={() => showDeleteModal({ user: user })}
              />
            )}
          </Menu>
        ),
      },
    ],
    [showEditUserSidebar, showDeleteModal, t]
  );

  if (!users) {
    return <Spinner />;
  }

  if (users.length === 0 && searchStr) {
    return <EmptySearchState />;
  }

  return (
    <Table<User>
      idColumn="shortId"
      searchStr={searchStr}
      data={users}
      filterProps={{
        addFilterText: t('core.filter'),
        config: [
          { id: 'name', type: 'text', icon: <IconPerson />, label: t('core.fullName') },
          { id: 'email', type: 'email', icon: <IconEmail />, label: t('core.email') },
          { id: 'phone', type: 'phone_number', icon: <IconPhone />, label: t('core.phone') },
          {
            id: 'role',
            type: 'single_select',
            icon: <IconRole />,
            label: t('role'),
            values: roleOptions.map((option) => ({ value: option.value, label: option.label })),
          },
        ],
      }}
      availableColumns={availableColumns}
      endFixedColumns={endFixedColumns}
      store={tableStore}
    />
  );
};

const InvitedUserList = ({ searchStr, users }: { searchStr: string; users: Array<User> }) => {
  const { t } = useScopedTranslate(locale);
  const showRevokeModal = useRevokeTeamUserModal();
  const { tableView, setTableView } = useTableView('invitedUserList');
  const tableStore = usePersistedTableStore({
    tableView,
    setTableView,
  });
  const roleOptions = useRoleOptions();

  const inviteMutation = useInviteUser({ onSuccess: hideModal });
  const availableColumns: Array<AvailableColumn<User>> = useMemo(
    () => [
      {
        id: 'email',
        title: t('core.email'),
        type: 'email',
      },
      {
        id: 'role',
        title: t('role'),
        renderHeaderIcon: () => <IconRole size={16} />,
        type: 'custom',
        Cell: ({ row: user }) => (
          <Badge
            variant={getRoleVariant(user.role)}
            text={capitalizeFirstLetter(user.role)}
            size="md"
          />
        ),
      },
      {
        id: 'inviteDate',
        title: t('invitedDate'),
        type: 'date',
      },
    ],
    [t]
  );

  const endFixedColumns: TableProps<User>['endFixedColumns'] = useMemo(
    () => [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: ({ row: user }) => (
          <Menu
            trigger={IconButton}
            triggerProps={{ variant: 'ghost-purple', size: 'md', icon: <IconMoreVertical /> }}
          >
            <Menu.Item
              label={t('reinviteMember')}
              icon={<IconRedo />}
              onSelect={() => inviteMutation.mutate(user.id)}
            />
            <Menu.Item
              label={t('revokeMember')}
              variant="danger"
              icon={<IconTrash />}
              onSelect={() => showRevokeModal({ user: user })}
            />
          </Menu>
        ),
      },
    ],
    [inviteMutation, showRevokeModal, t]
  );

  if (!users) {
    return <Spinner />;
  }

  if (!users.length) {
    return searchStr ? (
      <EmptySearchState />
    ) : (
      <EmptyState
        header={t('noActiveInvites')}
        content={t('inviteATeamMate')}
        icon={<IconSwap size={80} />}
      />
    );
  }

  return (
    <Table<User>
      idColumn="shortId"
      searchStr={searchStr}
      data={users}
      filterProps={{
        addFilterText: t('core.filter'),
        config: [
          { id: 'email', type: 'email', icon: <IconEmail />, label: t('core.email') },
          {
            id: 'role',
            type: 'single_select',
            icon: <IconRole />,
            label: t('role'),
            values: roleOptions.map((option) => ({ value: option.value, label: option.label })),
          },
          { id: 'inviteDate', type: 'date', icon: <IconDate />, label: t('invitedDate') },
        ],
      }}
      availableColumns={availableColumns}
      endFixedColumns={endFixedColumns}
      store={tableStore}
    />
  );
};

const RolesTabActions = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <IconButton
      icon={<IconHelp />}
      variant="ghost-purple"
      href="https://help.journeys.50skills.app/en/articles/562319-user-roles"
      target="_blank"
      aria-label={t('helpArticle')}
    />
  );
};

const UserListActions = ({
  searchStr,
  setSearchStr,
}: {
  searchStr: string;
  setSearchStr: (searchStr: string) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { canManageUsers } = usePermissions();

  const showAddTravellerSidebar = useTeamUserSidebar();

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearchStr} defaultValue={searchStr} />
      {canManageUsers && (
        <Button
          variant="primary"
          text={t('inviteMembers')}
          iconLeft={<IconAddPlus />}
          onClick={() => {
            return showAddTravellerSidebar();
          }}
        />
      )}
    </Stack>
  );
};

export const locale = {
  en: {
    details: 'Details',
    members: 'Members',
    invitedTab: 'Invited',
    rolesTab: 'Roles',
    inviteMembers: 'Invite Members',
    deleteMember: 'Delete member',
    reinviteMember: 'Re-send invite',
    revokeMember: 'Revoke invite',
    status: 'Status',
    joined: 'Joined',
    created: 'Created',
    invited: 'Invited',
    invitedDate: 'Invited date',
    noNumberSet: 'No number added',
    noActiveInvites: 'No active invites',
    inviteATeamMate: 'Invite a teammate to work with you in Journeys',
    role: 'Role',
    helpArticle: 'Help article',
  },
};
